import { Component } from 'react'
import { reportErrorToAdmin, normalizeErrorForReporting } from '../lib/errorReporting'
import './ErrorBoundary.css'

export class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      flipped: false,
    }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error, flipped: false }
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo })
    reportErrorToAdmin('ReactErrorBoundary', normalizeErrorForReporting(error), {
      componentStack: errorInfo?.componentStack || '',
    })
  }

  handleSceneClick = () => {
    this.setState((s) => ({ flipped: !s.flipped }))
  }

  render() {
    if (this.state.hasError) {
      const err = this.state.error
      const info = this.state.errorInfo
      const stackText = [
        err?.message || 'Unknown error',
        err?.stack ? `\n\n${err.stack}` : '',
        info?.componentStack ? `\n\nComponent stack:${info.componentStack}` : '',
      ]
        .filter(Boolean)
        .join('')

      return (
        <div
          className="error-boundary-overlay error-boundary-overlay--marketing-light"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="error-boundary-title"
        >
          <div
            className="error-boundary-scene"
            onClick={this.handleSceneClick}
            aria-label="Click to flip the card and show or hide technical details"
          >
            <div className={`error-boundary-inner${this.state.flipped ? ' is-flipped' : ''}`}>
              <div className="error-boundary-face error-boundary-face--front">
                <div className="error-boundary-icon" aria-hidden="true">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 8v4M12 16h.01" />
                  </svg>
                </div>
                <h2 id="error-boundary-title" className="error-boundary-title">
                  Something went wrong
                </h2>
                <p className="error-boundary-text">
                  We hit a problem loading this screen. You can try again in a moment. If it keeps happening, contact support and we will help.
                </p>
                <p className="error-boundary-hint">Click the card to see technical details</p>
                <div className="error-boundary-actions">
                  <button
                    type="button"
                    className="error-boundary-btn error-boundary-btn--primary"
                    onClick={(e) => {
                      e.stopPropagation()
                      window.location.reload()
                    }}
                  >
                    Refresh page
                  </button>
                  <button
                    type="button"
                    className="error-boundary-btn error-boundary-btn--ghost"
                    onClick={(e) => {
                      e.stopPropagation()
                      this.setState({ flipped: true })
                    }}
                  >
                    Technical details
                  </button>
                  <a
                    className="error-boundary-btn error-boundary-btn--ghost"
                    href="https://primedocumentsign.com/contact"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Contact support
                  </a>
                </div>
              </div>
              <div className="error-boundary-face error-boundary-face--back">
                <p className="error-boundary-back-header">Technical details</p>
                <pre className="error-boundary-pre">{stackText}</pre>
                <button
                  type="button"
                  className="error-boundary-btn error-boundary-btn--ghost"
                  onClick={(e) => {
                    e.stopPropagation()
                    this.setState({ flipped: false })
                  }}
                >
                  Back to message
                </button>
                <p className="error-boundary-back-footer">Or click the card to flip</p>
              </div>
            </div>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}
